/* Hero-image override for the PDP.

   WHY THIS EXISTS
   Horizon reacts to a variant change by refetching the WHOLE page and morphing
   it in. On this PDP that response is 3.4MB, about half of it inline app JS,
   and the gallery carries the media for every variant - 200 slides, 800 img
   elements. Measured time for the hero image to change: 25s originally, 11s
   once the sticky bar stopped starving the page. This fetches ONLY the product
   section (~1.1MB in ~700ms), pulls the new gallery's first image out with a
   regex rather than parsing 1.1MB into a DOM, and writes it into the active
   slide. The theme's own refresh lands later and agrees.

   It is a workaround. The real fix is the 3.4MB page.

   THE CENTRAL PROBLEM: this borrows a slide that belongs to the gallery. Every
   bug it has had comes back to that, and the two obvious defences are each
   wrong in a way that is only visible from the shopper's seat.

     Writing once and remembering it was handled (an appliedKey short-circuit)
     corrupts the gallery. Slides are reused: the slide holding Maranello Red's
     image gets overwritten with Sicilian Olive's, and when the gallery later
     makes that same slide active for Maranello Red the script decides it has
     already handled that selection and writes nothing - olive on screen, red on
     the label.

     Re-asserting from a MutationObserver on the gallery fixes that and breaks
     something worse: paging through a variant's OTHER images is a gallery
     mutation too. The observer could not tell browsing from a variant change,
     so every swipe to image 2 was immediately rewritten back to image 1.

   So: no observer, and nothing is left overwritten. Exactly one slide is ever
   borrowed at a time, its original attributes are kept, and it is put back
   before the next one is borrowed - and only if it still holds what this script
   wrote, so a slide the theme has since re-rendered is left alone. The gallery
   is never touched except in the instant a selection changes, which is the only
   moment this script has any business in it.

   OTHER THINGS THAT BIT, worth keeping in mind:

   - Target the ACTIVE slide. Exactly one carries aria-hidden="false".
   - Do not abort on every change event; one click fires several as the theme
     morphs, and aborting on each meant the fetch never completed.
   - It ran one selection behind for two reasons. The model id was read with
     `select option[selected]`, which matches the HTML ATTRIBUTE - that records
     what the server rendered and never moves when the shopper changes the
     select. And the colour is chosen in the Rubik app's SHADOW ROOT, where
     `change` does not cross the boundary (change is composed: false), so the
     listener never saw it. Clicks are composed, so composedPath() catches it.
*/
(() => {
  const main = document.getElementById('MainContent');
  if (!main) return;

  const liveHero = () => {
    const gal = main.querySelector('media-gallery');
    if (!gal) return null;
    return gal.querySelector('slideshow-slide[aria-hidden="false"] img')
        || gal.querySelector('slideshow-slide:not([aria-hidden="true"]) img')
        || gal.querySelector('img');
  };

  const sectionId = () => {
    const el = main.querySelector('[id^="shopify-section-"]');
    return el ? el.id.replace('shopify-section-', '') : '';
  };

  const optionValueIds = () => {
    const ids = [];
    main.querySelectorAll('variant-picker select').forEach((sel) => {
      const opt = sel.selectedOptions && sel.selectedOptions[0];
      if (opt && opt.dataset.optionValueId) ids.push(opt.dataset.optionValueId);
    });
    main.querySelectorAll('variant-picker fieldset input:checked').forEach((i) => {
      if (i.dataset.optionValueId) ids.push(i.dataset.optionValueId);
    });
    return ids;
  };

  const currentKey = () => optionValueIds().join(',');

  const cache = Object.create(null);
  let abort = null;
  let inflightKey = null;

  /* The one slide currently borrowed, with what it held before. */
  let borrowed = null;

  const giveBack = () => {
    if (!borrowed) return;
    const b = borrowed;
    borrowed = null;
    if (!b.img || !b.img.isConnected) return;
    /* If the theme has re-rendered this slide since, its own value is the right
       one - putting the old attributes back would undo correct work. */
    if (b.img.getAttribute('src') !== b.wrote) return;
    const set = (name, value) => {
      if (value === null) b.img.removeAttribute(name);
      else b.img.setAttribute(name, value);
    };
    set('src', b.src);
    set('srcset', b.srcset);
    set('sizes', b.sizes);
  };

  const apply = (key) => {
    const next = cache[key];
    if (!next) return;
    /* Already showing this selection on a slide that is still in the document:
       nothing to do, and nothing to disturb. */
    if (borrowed && borrowed.key === key && borrowed.img.isConnected
        && borrowed.img.getAttribute('src') === next) return;

    giveBack();

    const live = liveHero();
    if (!live) return;
    if ((live.getAttribute('src') || '') === next) return;

    borrowed = {
      key: key,
      img: live,
      wrote: next,
      src: live.getAttribute('src'),
      srcset: live.getAttribute('srcset'),
      sizes: live.getAttribute('sizes')
    };
    live.removeAttribute('srcset');
    live.removeAttribute('sizes');
    live.setAttribute('src', next);
    window.__sutiHero = { file: next.split('?')[0].split('/').pop(), at: Math.round(performance.now()) };
  };

  const pushHero = () => {
    const sid = sectionId();
    const key = currentKey();
    if (!sid || !key) return;

    if (cache[key]) { apply(key); return; }
    if (key === inflightKey) return;

    if (abort) abort.abort();
    abort = new AbortController();
    const mine = abort;
    inflightKey = key;

    fetch(location.pathname + '?section_id=' + sid + '&option_values=' + key, { signal: mine.signal })
      .then((r) => r.text())
      .then((html) => {
        const match = html.match(/<media-gallery[\s\S]*?<img[^>]*\ssrc="([^"]+)"/i);
        if (!match) return;
        cache[key] = match[1];
        /* The shopper may have moved on while this was in flight; painting now
           would show a colour they already left. The result is still cached. */
        if (currentKey() !== key) return;
        apply(key);
      })
      .catch(() => { /* aborted or offline: the theme's own refresh still lands */ })
      .finally(() => { if (inflightKey === key) inflightKey = null; });
  };

  /* The pickers do not settle synchronously - Rubik re-renders its shadow
     content and the theme morphs the light DOM behind it. These repeats are the
     ONLY times the gallery is touched. */
  let timers = [];
  const schedule = () => {
    timers.forEach(clearTimeout);
    timers = [120, 350, 800, 1500, 3000].map((ms) => setTimeout(pushHero, ms));
  };

  main.addEventListener('change', (event) => {
    if (!event.target || !event.target.closest) return;
    if (!event.target.closest('variant-picker')) return;
    pushHero();
    schedule();
  });

  /* Clicks cross shadow boundaries, change events do not. This is what catches
     the Rubik swatches and its model dropdown. Gallery arrows and thumbnails
     are deliberately NOT in here: browsing a variant's images must not trigger
     a rewrite. */
  document.addEventListener('click', (event) => {
    const path = event.composedPath ? event.composedPath() : [];
    for (let i = 0; i < path.length; i++) {
      const node = path[i];
      if (!node || !node.tagName) continue;
      const tag = node.tagName.toLowerCase();
      if (tag === 'rubik-swatch' || tag === 'variant-picker') { schedule(); return; }
    }
  }, true);

  window.sutiPushHero = pushHero;
})();
